import React, { useEffect, useRef, useState, useMemo } from "react";
import { Drawer, Box, IconButton, Typography, Avatar, CircularProgress, Chip, Stack, Badge, Fade, Paper, useMediaQuery, Dialog } from '@mui/material';
import { useTheme, alpha, styled } from '@mui/material/styles';
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useQuery, useMutation } from "react-query";
import client from "../../../../../API";
import CampaignChatScreen from "../../../../../Components/ChatComponents/CampaignChatScreen";
import ParticipantsPanel from "../../../../../Components/ChatComponents/chatextra";
import ConversationDetailsPanel from "../../../../../Components/ChatComponents/conversationdetailspanel";

// Z-index constants
const Z_INDEX = {
  CHAT_DRAWER: 1300,
  PARTICIPANTS_DRAWER: 1400,
  DETAILS_DIALOG: 1500,
};

// Styled components for better fullscreen experience
const FullscreenDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: Z_INDEX.CHAT_DRAWER,
  '& .MuiDrawer-paper': {
    height: '100vh',
    width: '100vw',
    maxHeight: '100vh',
    maxWidth: '100vw',
    borderRadius: 0,
    background: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // iOS safe area handling
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  backdropFilter: 'blur(10px)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  flexShrink: 0,
  minHeight: 64,
}));

const ChipsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  background: alpha(theme.palette.background.paper, 0.8),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  backdropFilter: 'blur(8px)',
  flexShrink: 0,
}));

const ChatContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
});

export default function CreatorChatDrawer({
  open,
  onClose,
  campaignId,
  creatorId,
  creatorName,
  creatorPfp,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const chatRef = useRef(null);

  /* ------------------------------------------------------------------
   * 1. bootstrap / create conversation
   * ----------------------------------------------------------------- */
  const { data: check, refetch } = useQuery(
    ["checkConversation", campaignId, creatorId],
    () =>
      client.conversations.checkCampaign({
        campaign_id: campaignId,
        creator_id: creatorId,
      }),
    { enabled: open }
  );

  const createConversation = useMutation(client.conversations.createCampaign, {
    onSuccess: () => refetch(),
  });

  useEffect(() => {
    if (open && check && !check.conversation_id && !createConversation.isLoading)
      createConversation.mutate({ campaign_id: campaignId, creator_id: creatorId });
  }, [open, check, createConversation.isLoading, campaignId, creatorId]);

  const conversationId = check?.conversation_id;

  /* ------------------------------------------------------------------
   * 2. pull campaign + creator details (for chips)
   * ----------------------------------------------------------------- */
  const { data: campaignData } = useQuery(
    ["campaignForChat", campaignId],
    () => client.campaigns.details(campaignId),
    { enabled: open }
  );

  const creatorData = useMemo(() => {
    if (!campaignData) return undefined;
    return campaignData.creators.find((c) => c.id === creatorId);
  }, [campaignData, creatorId]);

  const parentVideos = creatorData
    ? creatorData.videos.filter((v) => v.parentId == null)
    : [];
  const crossposts = creatorData
    ? creatorData.videos.filter((v) => v.parentId != null)
    : [];

  /* ------------------------------------------------------------------
   * 3. participants drawer toggle
   * ----------------------------------------------------------------- */
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const toggleParticipants = () => setParticipantsOpen((p) => !p);

  /* ------------------------------------------------------------------
   * 4. conversation details panel toggle
   * ----------------------------------------------------------------- */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const toggleDetails = () => setDetailsOpen((p) => !p);

  /* ------------------------------------------------------------------
   * 5. badge for ineligible deliverables
   * ----------------------------------------------------------------- */
  const [ineligibleCount, setIneligibleCount] = useState(0);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const scrollY = window.scrollY;
      
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      document.body.style.right = '0';

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.left = '';
        document.body.style.right = '';
        
        if (scrollY) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [open]);

  return (
    <>
      {/* =============== FULLSCREEN CHAT DRAWER =============== */}
      <FullscreenDrawer
        anchor="right"
        open={open}
        onClose={onClose}
        variant="temporary"
        disablePortal
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
        }}
        transitionDuration={300}
      >
        <Fade in={open} timeout={300}>
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* -------- Enhanced Header -------- */}
            <HeaderContainer>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: 'space-between',
                }}
              >
                {/* Left side - Back/Close and Creator info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                  <IconButton 
                    onClick={onClose}
                    sx={{ 
                      bgcolor: alpha(theme.palette.action.hover, 0.1),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.2),
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    {isMobile ? <ArrowBackIcon /> : <CloseIcon />}
                  </IconButton>
                  
                  <Avatar 
                    src={creatorPfp} 
                    alt={creatorName}
                    sx={{ 
                      width: 48, 
                      height: 48,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      boxShadow: 2,
                    }}
                  />
                  
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="600"
                      noWrap
                      sx={{ color: 'text.primary' }}
                    >
                      @{creatorName}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      Campaign Chat
                      {conversationId && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                </Box>

                {/* Right side - Action buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* deliverable badge */}
                  <IconButton
                    color="secondary"
                    disabled={ineligibleCount === 0}
                    onClick={() => chatRef.current?.scrollToFirstDeliverable?.()}
                    sx={{
                      bgcolor: ineligibleCount > 0 ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                      '&:hover': {
                        bgcolor: ineligibleCount > 0 ? alpha(theme.palette.error.main, 0.2) : alpha(theme.palette.action.hover, 0.1),
                      }
                    }}
                  >
                    <Badge
                      badgeContent={ineligibleCount}
                      color="error"
                      invisible={ineligibleCount === 0}
                      max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }
                      }}
                    >
                      <AssignmentTurnedInIcon />
                    </Badge>
                  </IconButton>

                  {/* participants toggle */}
                  {conversationId && (
                    <IconButton 
                      color="primary" 
                      onClick={toggleParticipants}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <PersonAddIcon />
                    </IconButton>
                  )}

                  {/* Conversation details toggle */}
                  {conversationId && (
                    <IconButton
                      onClick={toggleDetails}
                      sx={{
                        bgcolor: alpha(theme.palette.action.hover, 0.1),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.action.hover, 0.2),
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </HeaderContainer>

            {/* -------- Enhanced Chips Row -------- */}
            {creatorData && (
              <ChipsContainer>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Chip
                    color="primary"
                    variant="filled"
                    label={`Offer: $${Number(creatorData.price).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      boxShadow: 1,
                    }}
                  />
                  {parentVideos.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600, color: 'text.secondary' }}>
                        Promotions ({parentVideos.length}):
                      </Typography>
                      {parentVideos.map((v) => (
                        <Chip
                          key={v.id}
                          color="primary"
                          variant="outlined"
                          size="small"
                          label={`${v.promotionPlatform} – ${v.promotionType}`}
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </>
                  )}
                  {crossposts.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600, color: 'text.secondary' }}>
                        Crossposts ({crossposts.length}):
                      </Typography>
                      {crossposts.map((v) => (
                        <Chip
                          key={v.id}
                          color="secondary"
                          variant="outlined"
                          size="small"
                          label={v.promotionPlatform}
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </>
                  )}
                </Box>
              </ChipsContainer>
            )}

            {/* -------- Chat Body -------- */}
            <ChatContainer>
              {conversationId ? (
                <Fade in timeout={400}>
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CampaignChatScreen
                      ref={chatRef}
                      conversationId={conversationId}
                      campaignId={campaignId}
                      creatorId={creatorId}
                      sx={{ 
                        flex: 1, 
                        minHeight: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      showWarning={false} // Hide warning since we show campaign context in header
                      margin={isMobile ? 1 : 2}
                      chatSpacing={isMobile ? 0.5 : 1}
                    />
                  </Box>
                </Fade>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: 'column',
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    p: 4,
                    textAlign: 'center',
                  }}
                >
                  <CircularProgress size={60} thickness={4} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Setting up campaign chat...
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Please wait while we prepare your conversation with @{creatorName}
                  </Typography>
                </Box>
              )}
            </ChatContainer>
          </Box>
        </Fade>
      </FullscreenDrawer>

      {/* =============== PARTICIPANTS DRAWER =============== */}
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={participantsOpen}
        onClose={toggleParticipants}
        sx={{ zIndex: Z_INDEX.PARTICIPANTS_DRAWER }}
        PaperProps={{
          sx: isMobile
            ? { 
                height: "85vh", 
                borderTopLeftRadius: 16, 
                borderTopRightRadius: 16,
                boxShadow: 4,
              }
            : { 
                width: 380, 
                height: "100%",
                borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <ParticipantsPanel
            conversationId={conversationId}
            onClose={toggleParticipants}
            sx={{ flex: 1, minHeight: 0 }}  
          />
        </Box>
      </Drawer>

      {/* =============== CONVERSATION DETAILS DIALOG =============== */}
      <Dialog
        open={detailsOpen}
        onClose={toggleDetails}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{ zIndex: Z_INDEX.DETAILS_DIALOG }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            height: { xs: '85vh', sm: '80vh' },
            maxHeight: { xs: '85vh', sm: '80vh' },
          }
        }}
      >
        <ConversationDetailsPanel
          conversationId={conversationId}
          onClose={toggleDetails}
          // Pass campaign-specific props
          campaignId={campaignId}
          creatorId={creatorId}
          campaignData={campaignData}
          creatorData={creatorData}
        />
      </Dialog>
    </>
  );
}